import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { flattenPlanExercises } from './planExerciseList';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

interface IExerciseInstance {
  id?: number;
  rowIndex?: number;
  exercise?: { name?: string } | null;
}

interface IExerciseGroup {
  groupNumber?: number;
  groupType?: string;
  isRestPeriod?: boolean;
  exercises?: IExerciseInstance[];
}

interface IInstanceTemplate {
  id: number;
  personalizedNotes?: string;
  groups?: IExerciseGroup[];
}

interface IClient {
  id: number;
  name?: string;
}

interface IClientWorkout {
  clientSubscription?: { client?: IClient };
}

export interface IPlan {
  id: number;
  planName: string;
  workoutInstanceTemplates: IInstanceTemplate[];
  clientWorkouts?: IClientWorkout[];
}

interface IPlanCardProps {
  plan: IPlan;
  isSelected: boolean;
  onToggleSelect: (checked: boolean) => void;
  onOpenDetails: (workoutInstanceId: number) => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: (workoutInstanceId: number) => void;
  onAssign: () => void;
  /**
   * #10: lo decide UN switch arriba de la página, no cada card. Enzo: "El toggle
   * es para todas las sesiones o para ninguna".
   */
  showExercises: boolean;
}

const CLIENT_COLORS = ['#4d7c0f', '#22c55e', '#f59e0b', '#ef4444', '#0ea5e9', '#a855f7'];

const getInitials = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').substring(0, 2).toUpperCase();
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase();
};

const colorForIndex = (i: number) => CLIENT_COLORS[i % CLIENT_COLORS.length];

export function PlanCard({
  plan,
  isSelected,
  onToggleSelect,
  onOpenDetails,
  onView,
  onEdit,
  onDelete,
  onAssign,
  showExercises
}: IPlanCardProps) {
  const intl = useIntl();
  const instance = plan.workoutInstanceTemplates[0];
  const instanceId = instance?.id;

  // #10: la card ya trae los grupos, asi que el listado no necesita otro fetch.
  const exerciseList = useMemo(() => flattenPlanExercises(instance?.groups), [instance]);

  const { groupCount, exerciseCount } = useMemo(() => {
    const groups = instance?.groups ?? [];
    return {
      groupCount: groups.length,
      exerciseCount: groups.reduce((sum, g) => sum + (g.exercises?.length ?? 0), 0)
    };
  }, [instance]);

  const clients = useMemo(() => {
    return (plan.clientWorkouts ?? [])
      .map((cw) => cw.clientSubscription?.client)
      .filter((c): c is IClient => Boolean(c && c.id));
  }, [plan.clientWorkouts]);

  const notes = instance?.personalizedNotes?.trim();

  return (
    <div
      style={{
        background: 'var(--ios-card-bg)',
        border: `1px solid ${isSelected ? '#a3e635' : 'var(--ios-card-border)'}`,
        borderRadius: 'var(--ios-radius-lg)',
        boxShadow: isSelected ? '0 0 0 3px rgba(163, 230, 53, 0.30)' : 'var(--ios-card-shadow)',
        padding: '1rem',
        transition: 'var(--ios-transition)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        height: '100%'
      }}
    >
      {/* Header: checkbox + name */}
      <div className="flex align-items-start gap-2">
        <Checkbox
          checked={isSelected}
          onChange={(e) => onToggleSelect(Boolean(e.checked))}
          style={{ marginTop: '0.2rem' }}
        />
        <button
          type="button"
          onClick={() => instanceId && onOpenDetails(instanceId)}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            textAlign: 'left',
            cursor: instanceId ? 'pointer' : 'default',
            color: 'var(--ios-text)',
            fontSize: '1.05rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            flex: 1,
            wordBreak: 'break-word'
          }}
        >
          {plan.planName}
        </button>
      </div>

      {/* Stats chips */}
      <div className="flex flex-wrap gap-2">
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            padding: '0.2rem 0.6rem',
            background: 'rgba(163, 230, 53, 0.18)',
            color: '#4d7c0f',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: 'var(--ios-radius-pill)'
          }}
        >
          <i className="pi pi-th-large" style={{ fontSize: '0.7rem' }} />
          {intl.formatMessage({ id: 'plansPage.card.groups' }, { count: groupCount })}
        </span>
        {/* #10: el chip solo informa cuántos ejercicios tiene. El listado lo abre
            el switch de la página, no la card. */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            padding: '0.2rem 0.6rem',
            background: 'rgba(34,197,94,0.1)',
            color: '#22c55e',
            fontSize: '0.72rem',
            fontWeight: 600,
            borderRadius: 'var(--ios-radius-pill)'
          }}
        >
          <i className="pi pi-bolt" style={{ fontSize: '0.7rem' }} />
          {intl.formatMessage({ id: 'plansPage.card.exercises' }, { count: exerciseCount })}
        </span>
        {clients.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              padding: '0.2rem 0.6rem',
              background: 'rgba(245,158,11,0.1)',
              color: '#f59e0b',
              fontSize: '0.72rem',
              fontWeight: 600,
              borderRadius: 'var(--ios-radius-pill)'
            }}
          >
            <i className="pi pi-users" style={{ fontSize: '0.7rem' }} />
            {intl.formatMessage({ id: 'plansPage.card.assignedCount' }, { count: clients.length })}
          </span>
        )}
      </div>

      {/* #10: listado de ejercicios en orden de ejecucion */}
      {showExercises && exerciseList.length > 0 && (
        <ol
          style={{
            listStyle: 'none',
            margin: 0,
            padding: '0.6rem 0.75rem',
            background: 'var(--ios-surface-subtle)',
            borderRadius: 'var(--ios-radius-md)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.35rem',
            maxHeight: '13rem',
            overflowY: 'auto'
          }}
        >
          {exerciseList.map((item) => (
            <li
              key={item.key}
              style={{
                display: 'flex',
                gap: '0.5rem',
                fontSize: '0.8rem',
                color: 'var(--ios-text-secondary)',
                lineHeight: 1.35
              }}
            >
              <span
                style={{
                  color: 'var(--ios-text-tertiary)',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  minWidth: '1.6rem',
                  textAlign: 'right',
                  flex: '0 0 auto'
                }}
              >
                {item.label}
              </span>
              <span style={{ wordBreak: 'break-word' }}>{item.name}</span>
            </li>
          ))}
        </ol>
      )}

      {/* Notes */}
      {notes && (
        <p
          style={{
            margin: 0,
            fontSize: '0.82rem',
            color: 'var(--ios-text-secondary)',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
          title={notes}
        >
          {notes}
        </p>
      )}

      {/* Assigned clients */}
      {clients.length > 0 && (
        <div className="flex align-items-center gap-2" style={{ minHeight: '1.8rem' }}>
          <div style={{ display: 'flex' }}>
            {clients.slice(0, 3).map((c, idx) => (
              <div
                key={c.id}
                title={c.name}
                style={{
                  width: '1.7rem',
                  height: '1.7rem',
                  borderRadius: '50%',
                  background: colorForIndex(idx),
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  border: '2px solid var(--ios-card-bg)',
                  marginLeft: idx === 0 ? 0 : '-0.45rem',
                  zIndex: 3 - idx
                }}
              >
                {getInitials(c.name)}
              </div>
            ))}
          </div>
          <span style={{ fontSize: '0.75rem', color: 'var(--ios-text-tertiary)', fontWeight: 500 }}>
            {clients.length <= 3
              ? clients
                  .map((c) => c.name)
                  .filter(Boolean)
                  .join(', ')
              : intl.formatMessage(
                  { id: 'plansPage.card.clientsSummary' },
                  {
                    first: clients[0]?.name ?? '',
                    rest: clients.length - 1
                  }
                )}
          </span>
        </div>
      )}

      {/* Actions */}
      <div
        className="flex align-items-center justify-content-end gap-1"
        style={{
          borderTop: '1px solid var(--ios-divider)',
          paddingTop: '0.6rem',
          marginTop: 'auto'
        }}
      >
        {/* #10: desasignar desde acá se dio de baja junto con los chips por
            alumno (decisión de Luciano, 30/08). Queda solo asignar. */}
        <Button
          icon="pi pi-user-plus"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.assign' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onAssign}
          style={{ color: '#22c55e' }}
        />
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.view' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onView}
          style={{ color: 'var(--ios-text-secondary)' }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.edit' })}
          tooltipOptions={{ position: 'top' }}
          onClick={onEdit}
          style={{ color: 'var(--ios-text-secondary)' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm"
          tooltip={intl.formatMessage({ id: 'common.delete' })}
          tooltipOptions={{ position: 'top' }}
          onClick={() => instanceId && onDelete(instanceId)}
          style={{ color: '#ef4444' }}
        />
      </div>
    </div>
  );
}
